import { Controller } from "@hotwired/stimulus"

// Fades the video in over the static SVG screenshot once it can play, and keeps
// playback alive whenever the video is actually visible.
//
// The landing hero renders TWO phones (light + dark); the inactive one sits in
// `display:none`, so its `autoplay` never fires and it freezes on frame 0.
// Flipping the theme (CSS-only, no reload) then reveals that frozen video.
// An IntersectionObserver fixes this by design: the moment a video becomes
// visible (theme flip, locale nav, or scroll-into-view) we (re)start playback
// and reveal it. A shown-but-frozen video is therefore impossible. If playback
// is blocked (autoplay policy) the static screenshot underneath stays visible.
export default class extends Controller {
  static targets = ["video"]

  connect() {
    if (!this.hasVideoTarget) return

    // Video may already be ready (cached / fast network) before connect runs.
    if (this.videoTarget.readyState >= 3) this.reveal()

    this.observer = new IntersectionObserver(
      (entries) => entries.forEach((entry) => entry.isIntersecting && this.#start()),
      { threshold: 0.1 }
    )
    this.observer.observe(this.videoTarget)
  }

  disconnect() {
    this.observer?.disconnect()
  }

  reveal() {
    this.element.classList.add("video-ready")
  }

  // Start (or resume) playback for a video that just became visible. play()
  // returns a promise that rejects when autoplay is blocked — ignore it, the
  // screenshot remains as the fallback.
  #start() {
    const video = this.videoTarget
    if (!video.paused) return
    const playback = video.play()
    if (playback && typeof playback.catch === "function") playback.catch(() => {})
  }
}
